/**
 * FileUploader — componente reutilizable para subida de archivos con validación cliente.
 *
 * Props:
 *   - accept?: string                 — ej. "application/pdf,image/jpeg,image/png,image/webp"
 *   - maxSizeMB?: number              — default 8
 *   - allowedMimes?: string[]         — default ["application/pdf","image/jpeg","image/png","image/webp"]
 *   - onSelect: ({ base64, mime, filename, size }) => void   — payload listo para POST
 *   - onClear?: () => void
 *   - label?: string                  — texto del botón cuando no hay archivo
 *   - testId?: string                 — base para data-testid
 *
 * Devuelve un bloque compacto con dropzone + preview + botón limpiar.
 * El archivo NO se sube aquí — solo se valida y se transforma a base64 para que
 * el consumidor lo envíe en su propio endpoint.
 */
import { useRef, useState } from "react";
import { Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";

const DEFAULT_MIMES = ["application/pdf", "image/jpeg", "image/png", "image/webp"];

const MIME_FRIENDLY = {
  "application/pdf": "PDF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/webp": "WebP",
};

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      // strip "data:<mime>;base64," prefix — backend acepta ambos formatos, pero enviamos limpio.
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}

export function FileUploader({
  accept,
  maxSizeMB = 8,
  allowedMimes = DEFAULT_MIMES,
  onSelect,
  onClear,
  label = "Seleccionar archivo",
  testId = "file-uploader",
}) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const acceptAttr = accept || allowedMimes.join(",");
  const maxBytes = maxSizeMB * 1024 * 1024;
  const allowedLabel = allowedMimes
    .map((m) => MIME_FRIENDLY[m] || m)
    .filter(Boolean)
    .join(", ");

  const pick = () => inputRef.current?.click();

  const clear = () => {
    setFile(null);
    setErr("");
    if (inputRef.current) inputRef.current.value = "";
    onClear && onClear();
  };

  const handleFiles = async (f) => {
    if (!f) return;
    setErr("");
    if (!allowedMimes.includes(f.type)) {
      setErr(`Formato no admitido. Usa ${allowedLabel}.`);
      return;
    }
    if (f.size > maxBytes) {
      setErr(`El archivo supera ${maxSizeMB} MB. Por favor adjunta uno más pequeño.`);
      return;
    }
    if (f.size === 0) {
      setErr("El archivo está vacío.");
      return;
    }
    setBusy(true);
    try {
      const base64 = await readAsBase64(f);
      setFile({ name: f.name, size: f.size, mime: f.type });
      onSelect({ base64, mime: f.type, filename: f.name, size: f.size });
    } catch (e) {
      setErr("No se pudo procesar el archivo. Inténtalo de nuevo.");
    } finally {
      setBusy(false);
    }
  };

  const onChange = (e) => handleFiles(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files?.[0]);
  };

  const isImage = file?.mime?.startsWith("image/");

  return (
    <div data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={acceptAttr}
        className="hidden"
        onChange={onChange}
        data-testid={`${testId}-input`}
      />

      {!file ? (
        <button
          type="button"
          onClick={pick}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          disabled={busy}
          className="w-full border border-dashed border-stone-300 hover:border-stone-500 rounded-md px-4 py-3 text-sm text-stone-600 flex items-center justify-center gap-2 transition-colors"
          data-testid={`${testId}-trigger`}
        >
          <Paperclip className="w-4 h-4" />
          {busy ? "Procesando…" : label}
        </button>
      ) : (
        <div
          className="border border-stone-200 bg-stone-50 rounded-md px-3 py-2 flex items-center gap-3"
          data-testid={`${testId}-selected`}
        >
          {isImage ? <ImageIcon className="w-4 h-4 text-stone-500 shrink-0" /> : <FileText className="w-4 h-4 text-stone-500 shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" data-testid={`${testId}-filename`}>{file.name}</div>
            <div className="text-xs text-stone-500">{humanSize(file.size)}</div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-stone-500 hover:text-stone-900 p-1"
            aria-label="Quitar archivo"
            data-testid={`${testId}-clear`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {err && (
        <div
          className="text-xs text-red-700 mt-2"
          data-testid={`${testId}-error`}
        >
          {err}
        </div>
      )}

      {!file && !err && (
        <div className="text-xs text-stone-400 mt-1.5">
          {allowedLabel} · máx {maxSizeMB} MB
        </div>
      )}
    </div>
  );
}

export default FileUploader;
